import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useExistingPortfolioSnapshot } from "@/lib/hooks/use-existing-portfolio-snapshot";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { AssetAllocationChart } from "../asset-allocation-chart";
import { XRayPieChart } from "../xray-pie-chart";
import { T } from "./asset-allocation.i18n";
import { ComparisonLayout, ComparisonTable } from "./comparison-layout";
import type { PortfolioInfo } from "./types";

type AssetAllocationProps = {
  portfolioId: string | null;
  config: {
    style?: string;
    title?: string;
    description?: string;
    showChart?: boolean;
    showTable?: boolean;
  };
  language: string;
  chartColors?: string[] | null;
  portfolioInfo: PortfolioInfo;
  /**
   * Phase 2 PR-A (S3) — engine reported `kind: "snapshot"` for this component on this
   * portfolio. When set AND prerequisites are met but the asset_allocation snapshot row is
   * missing, replace the "asset allocation not available" fallback with a clear
   * snapshot-missing placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
  /**
   * QP-177 — show the existing-vs-modified comparison view. The renderer sets this to
   * `portfolio is a followup AND cfg.enableFollowupComparison === true`; when false the
   * existing-snapshot fetch is skipped entirely.
   */
  showFollowupComparison?: boolean;
  /** Agreement follow-ups label the comparison "Savings Plan". */
  comparisonVariant?: "portfolio" | "savingsPlan";
};

export function AssetAllocation({
  portfolioId,
  config,
  language,
  chartColors,
  portfolioInfo,
  hasValidationError = false,
  showFollowupComparison = false,
  comparisonVariant = "portfolio",
}: AssetAllocationProps) {
  const t = pickT(T, language);
  const assetAllocation = usePortfolioSnapshot("assetAllocation", portfolioId);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);
  const existingAllocation = useExistingPortfolioSnapshot(
    "assetAllocation",
    portfolioId,
    showFollowupComparison,
  );

  if (assetAllocation.isLoading || portfolio.isLoading) return null;

  if (!assetAllocation.data) {
    if (portfolioInfo.isCustomPortfolio) {
      if (!portfolio.data) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.noProductsAssetAllocation}
          </div>
        );
      }
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.assetAllocationCalculating}
        </div>
      );
    }
    if (!portfolio.data) {
      if (!portfolioInfo.hasRiskAssessment) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.completeRiskAssetAllocation}
          </div>
        );
      }
      // QL-311 — a missing amount blocks snapshot generation too; without this
      // branch it fell through to the no-portfolio-for-score message, blaming
      // collection configuration for a missing user input.
      if (!portfolioInfo.hasInvestmentAmount) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.enterInvestmentAmount}
          </div>
        );
      }
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noPortfolioForScore(String(portfolioInfo.riskScore ?? ""))}
        </div>
      );
    }
    // PR-A (S3) — D20 N/A means prereq branches above wouldn't trigger validation.
    // Reaching this fallback with hasValidationError=true means prereqs are met but the
    // asset_allocation snapshot row is missing (system-bug case).
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.assetAllocationNotAvailable}
      </div>
    );
  }

  const snapshot =
    typeof assetAllocation.data === "string"
      ? (() => {
          try {
            return JSON.parse(assetAllocation.data as string);
          } catch {
            return null;
          }
        })()
      : (assetAllocation.data as Record<string, any>);

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  const allocations = snapshot.allocations || [];
  const unclassifiedPct = snapshot.unclassifiedPct || 0;

  if (allocations.length === 0 && unclassifiedPct === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noData}
      </div>
    );
  }

  // QP-177 — followup comparison: show side-by-side with existing portfolio
  if (showFollowupComparison && existingAllocation.data && !existingAllocation.isLoading) {
    const existingSnapshot =
      typeof existingAllocation.data === "string"
        ? (() => {
            try {
              return JSON.parse(existingAllocation.data as string);
            } catch {
              return null;
            }
          })()
        : (existingAllocation.data as Record<string, any>);

    if (existingSnapshot && !existingSnapshot.error) {
      type Alloc = { assetClassId: string; assetClassName: string; percentage: number };
      const toChartData = (allocs: Alloc[], unclassified: number) => {
        const items = allocs.map((a) => ({
          id: a.assetClassId,
          label: a.assetClassName,
          value: a.percentage,
        }));
        if (unclassified > 0) {
          items.push({ id: "unclassified", label: t.unclassified, value: unclassified });
        }
        return items;
      };

      const modifiedChartData = toChartData(allocations, unclassifiedPct);
      const existingRaw: Alloc[] = Array.isArray(existingSnapshot.allocations)
        ? existingSnapshot.allocations
        : [];
      // Prefer the modified side's (flow-language) label for classes present on both
      // sides — the frozen existing snapshot may carry a different localization.
      const modifiedLabelById = new Map(modifiedChartData.map((d) => [d.id, d.label]));
      const existingChartData = toChartData(
        existingRaw,
        Number(existingSnapshot.unclassifiedPct) || 0,
      ).map((d) => ({ ...d, label: modifiedLabelById.get(d.id) ?? d.label }));

      if (existingChartData.length > 0) {
        // One color per asset class across BOTH pies and the comparison table.
        const palette = chartColors || TABLEAU20_COLORS;
        const colorById = new Map<string, string>();
        // Unclassified (incl. unmatchable holdings, QL-304 phase 1) is always
        // muted gray, never a palette color.
        colorById.set("unclassified", "#9ca3af");
        for (const item of [
          ...[...modifiedChartData].sort((a, b) => b.value - a.value),
          ...[...existingChartData].sort((a, b) => b.value - a.value),
        ]) {
          if (!colorById.has(item.id)) {
            colorById.set(item.id, palette[colorById.size % palette.length]);
          }
        }
        const existingColored = existingChartData.map((d) => ({
          ...d,
          color: colorById.get(d.id),
        }));
        const modifiedColored = modifiedChartData.map((d) => ({
          ...d,
          color: colorById.get(d.id),
        }));

        const comparisonCommonProps = {
          showChart: config.showChart !== false,
          showTable: false,
          chartColors,
          language,
          height: 220,
        };

        return (
          <VisualizationContainer config={config}>
            <ComparisonLayout
              variant={comparisonVariant}
              existingContent={
                <XRayPieChart
                  data={existingColored}
                  categoryLabel={t.categoryAssetClass}
                  {...comparisonCommonProps}
                />
              }
              modifiedContent={
                <XRayPieChart
                  data={modifiedColored}
                  categoryLabel={t.categoryAssetClass}
                  {...comparisonCommonProps}
                />
              }
            />
            <ComparisonTable
              variant={comparisonVariant}
              existingData={existingColored}
              modifiedData={modifiedColored}
              categoryLabel={t.categoryAssetClass}
            />
          </VisualizationContainer>
        );
      }
    }
  }

  return (
    <AssetAllocationChart
      data={allocations}
      unclassifiedPct={unclassifiedPct}
      timestamp={snapshot.timestamp}
      showChart={config.showChart !== false}
      showTable={config.showTable || false}
      chartColors={chartColors}
      style={config.style as any}
      title={config.title}
      description={config.description}
      language={language}
    />
  );
}
